import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck } from "lucide-react";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../i18n";

const lineVariants = {
    hidden: { y: "115%" },
    show: { y: 0, transition: { type: "spring", damping: 22, stiffness: 90 } },
};

const groupVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14, delayChildren: 0.2 } },
};

const fade = {
    hidden: { opacity: 0, y: 26 },
    show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const Phone = ({ src, alt, className, testId }) => (
    <div className={`overflow-hidden rounded-[2.6rem] border-[6px] border-[#0B2233] bg-[#0B2233] shadow-[0_40px_90px_rgba(0,0,0,0.45)] ${className}`} data-testid={testId}>
        <img src={src} alt={alt} className="h-full w-full object-cover" loading="eager" />
    </div>
);

export const Hero = ({ t, onNavigate }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const phoneY = useTransform(scrollYProgress, [0, 1], [0, 100]);

    return (
        <section
            ref={ref}
            id="top"
            className="relative overflow-hidden bg-gradient-to-br from-[#032A44] via-[#01537B] to-[#14778D] pb-20 pt-36 md:pb-28 md:pt-44"
            data-testid="hero-section"
        >
            <div aria-hidden="true" className="absolute -top-32 left-[-12%] h-[520px] w-[520px] rounded-full bg-[#3E8588]/25 blur-[140px]" />
            <div aria-hidden="true" className="absolute bottom-[-25%] right-[-8%] h-[480px] w-[480px] rounded-full bg-[#EDCD7C]/20 blur-[150px]" />

            <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
                <motion.div variants={groupVariants} initial="hidden" animate="show" className="lg:col-span-6">
                    <motion.p
                        variants={fade}
                        className="mb-8 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-[#5EEAD4] md:text-sm"
                        data-testid="hero-overline"
                    >
                        <span className="h-px w-10 bg-[#5EEAD4]" />
                        {t.hero.overline}
                    </motion.p>

                    <h1 className="font-display text-5xl font-medium leading-[1.08] tracking-tight text-white md:text-6xl lg:text-[4.6rem]" data-testid="hero-title">
                        <span className="sr-only">{t.hero.h1sr}</span>
                        <span className="block overflow-hidden pb-1">
                            <motion.span variants={lineVariants} className="block">
                                {t.hero.titleA}
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span variants={lineVariants} className="block italic text-[#F7EAB8]">
                                {t.hero.titleB}
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p variants={fade} className="mt-8 max-w-xl text-base leading-relaxed text-[#A3B3B8] md:text-lg" data-testid="hero-subtitle">
                        {t.hero.sub}
                    </motion.p>

                    <motion.div variants={fade} className="mt-10 flex flex-wrap items-center gap-4" data-testid="hero-store-badges">
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.03]"
                            data-testid="hero-badge-appstore"
                        >
                            <img src="/brand/badge-appstore.png" alt="Download on the App Store" className="h-12 w-auto md:h-14" />
                        </a>
                        <a
                            href={GOOGLE_PLAY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.03]"
                            data-testid="hero-badge-googleplay"
                        >
                            <img src="/brand/badge-googleplay.png" alt="Get it on Google Play" className="h-12 w-auto md:h-14" />
                        </a>
                    </motion.div>

                    <motion.div variants={fade} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-semibold text-[#8FA6AD] md:text-sm">
                        <span className="flex items-center gap-2" data-testid="hero-badge-available">
                            <BadgeCheck size={16} className="text-[#5EEAD4]" />
                            {t.hero.available}
                        </span>
                        <span className="flex items-center gap-2" data-testid="hero-badge-trial">
                            <span className="h-1 w-1 rounded-full bg-[#F7EAB8]" />
                            {t.hero.trialLine}
                        </span>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 90 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative flex justify-center lg:col-span-6"
                >
                    <motion.div style={{ y: phoneY }} className="relative flex items-end justify-center" data-testid="hero-phones">
                        <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[380px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#EDCD7C]/20 blur-[100px]" />
                        <Phone
                            src="/guide/hero-calendar.webp"
                            alt="TheraOffice — Ημερολόγιο συνεδριών"
                            className="relative z-0 -mr-10 hidden h-[420px] w-[205px] -rotate-6 sm:block md:h-[480px] md:w-[235px]"
                            testId="hero-phone-calendar"
                        />
                        <Phone
                            src="/guide/hero-home.webp"
                            alt="TheraOffice — Αρχική οθόνη"
                            className="relative z-10 h-[460px] w-[225px] rotate-2 md:h-[540px] md:w-[265px]"
                            testId="hero-phone-home"
                        />
                    </motion.div>
                </motion.div>
            </div>

            <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                onClick={() => onNavigate("features")}
                className="absolute bottom-6 left-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#8FA6AD] transition-colors duration-300 hover:text-white md:left-12"
                data-testid="hero-scroll-indicator"
            >
                <ArrowDown size={14} className="animate-bounce" />
                {t.hero.scroll}
            </motion.button>
        </section>
    );
};
